// © Microsoft Corporation. All rights reserved.

import { AccessibilityHelpers, enums, hasThemeBase, isComponentDisabled, isComponentInModal, preventSelection, addOrUpdateTelemetryObject, renderHybrid } from "@msnews/social-utils";

import CopyrightForm from "../copyright-form/CopyrightForm";
import GradientOverlay from "../common/gradient-overlay/GradientOverlay";
import { IRequestContext } from "@msnews/core";
import MultiLoginPage from "../multi-login-page/MultiLoginPage";
import NotificationTray from "../common/notification/NotificationTray";
import OnboardingPage from "../onboarding-page/OnboardingPage";
import OnboardingThirdPartyMsaPage from "../onboarding-page/OnboardingThirdPartyPage";
import OnboardingUnderagePage from "../onboarding-page/OnboardingUnderagePage";
import ProfileManageDataPage from "../profile/profile-manage-data-page/ProfileManageDataPage";
import PublisherSubscribeFollowButton from "../publisher-subscribe-follow-button/PublisherSubscribeFollowButton";
import React from "react";
import ReactionBreakdown from "../reaction-breakdown/ReactionBreakdown";
import ReportMenu from "../common/report-menu/ReportMenu";
import { SocialUnhandledProps } from "../Social.props";
import { classNames } from "@microsoft/fast-web-utilities";

const usedProps = ["component"];

interface MainProps extends SocialUnhandledProps {
    requestContext: IRequestContext;
}

export default class Main extends React.PureComponent<MainProps> {
    telemetryObjects: any;

    constructor(props) {
        super(props);

        this.telemetryObjects = {};
    }

    componentDidMount() {
        const isSuperApp = hasThemeBase(this.props.theme, enums.theme.superappBase);
        isSuperApp && this.preventSelectionInSuperapp();
    }

    preparePropsToPass(moduleName) {
        const propsToPass = {...this.props};
        usedProps.map(x => delete propsToPass[x]);

        // Add and pass a container telemetry object matching with the module
        const moduleTelemetryContract = { name: moduleName.toUpperCase() };
        this.telemetryObjects.module = addOrUpdateTelemetryObject(this.props.mainTelemetryObject, moduleTelemetryContract, {}, this.telemetryObjects.module);
        propsToPass.mainTelemetryObject = this.telemetryObjects.module;

        return propsToPass;
    }

    switchToComponent(moduleName, id) {
        if (!moduleName) { return null; }
        const propsToPass = this.preparePropsToPass(moduleName);
        moduleName = moduleName.toLowerCase();

        switch (moduleName) {
            case "reactionbreakdowncontent":
                return <ReactionBreakdown {...propsToPass} {...{contentId: propsToPass.id}} />;

            case "reactionbreakdowncomment":
                return <ReactionBreakdown {...propsToPass} {...{commentId: propsToPass.id}} />;

            case "multiloginpage":
                return <MultiLoginPage {...propsToPass}/>;

            case "onboardingpage":
                return <OnboardingPage {...propsToPass} />;

            case "onboardingthirdpartypage":
                return <OnboardingThirdPartyMsaPage {...propsToPass} />;

            case "onboardingunderagepage":
                return <OnboardingUnderagePage {...propsToPass} />;

            case "reportmenu":
                return <ReportMenu {...propsToPass} />;

            case "copyrightform":
                return <CopyrightForm {...propsToPass} {...{reportType: "Copyright"}} />;

            case "trademarkform":
                return <CopyrightForm {...propsToPass} {...{reportType: "Trademark"}} />;

            case "notificationtray":
                return <NotificationTray {...propsToPass} />;

            case "managedatapage":
                return <ProfileManageDataPage {...propsToPass} />;

            case "publishersubscribefollowbutton":
                return <PublisherSubscribeFollowButton {...propsToPass} />;

            case "gradientoverlay":
                return <GradientOverlay {...propsToPass} />;
        }

        return null;
    }

    componentDidUpdate() {
        AccessibilityHelpers.attachAccessibilityListeners(document);
    }

    render() {
        const isSuperApp = hasThemeBase(this.props.theme, enums.theme.superappBase);
        const { mainContainer, mainSuperAppContainer, mainInSuperappModal} = this.props.managedClasses;
        const isRenderedInSuperappModal = isComponentInModal(this.props.theme, this.props.moduleName);
        const notificationPosition = this.props.commentDisplayType === "overlay" ? "left" : "right";

        const componentToRender = this.switchToComponent(this.props.moduleName, this.props.id);

        return (
            <div
                className={classNames(
                    mainContainer,
                    [mainSuperAppContainer, isSuperApp],
                    ["no-select", isSuperApp],
                    [mainInSuperappModal, isRenderedInSuperappModal]
                )}
                {...renderHybrid(this.telemetryObjects && this.telemetryObjects.module, false)}
            >
                { componentToRender }
                { componentToRender != null && !isComponentDisabled(this.props.theme, "notificationtray") && <NotificationTray {...{...this.props, alignment: notificationPosition}}  /> }
            </div>
        );
    }

    preventSelectionInSuperapp() {
        document.addEventListener("touchstart", preventSelection);
    }
}
